import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuthStore } from '../store/auth';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setAuth(data.token, data.user, data.tenant);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - decorative */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 relative overflow-hidden">
        {/* Abstract shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-[15%] -left-20 w-80 h-80 bg-brand-400/20 rounded-full blur-3xl" />
          <div className="absolute bottom-[20%] -right-20 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl" />
          <div className="absolute top-[50%] left-[30%] w-64 h-64 bg-brand-300/10 rounded-full blur-3xl" />
        </div>
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-8 shadow-lg shadow-black/10">
            <span className="text-2xl font-extrabold text-white">S</span>
          </div>
          <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
            Ihr digitaler<br />Handwerksbetrieb
          </h2>
          <p className="text-brand-200 text-base leading-relaxed max-w-sm">
            Projekte, Angebote, Rechnungen und Zeiterfassung — alles in einer Plattform.
          </p>
          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 mt-8">
            {['GAEB Import', 'DATANORM', 'XRechnung', 'KI-Assistent'].map((f) => (
              <span key={f} className="px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white/80 text-xs font-medium rounded-full border border-white/10">
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
        <div className="w-full max-w-[400px] animate-fade-in">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-glow">
              <span className="text-white text-sm font-extrabold">S</span>
            </div>
            <span className="text-xl font-bold text-slate-900">samoPlan</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Willkommen zurück</h1>
          <p className="text-slate-400 mt-1 mb-8">Melden Sie sich in Ihrem Konto an</p>

          {error && (
            <div className="flex items-start gap-3 bg-error-50 border border-error-100 text-error-600 p-4 rounded-xl mb-6 text-sm animate-scale-in">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="sp-label">E-Mail-Adresse</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="sp-input" placeholder="name@firma.de" required autoFocus
              />
            </div>

            <div>
              <label className="sp-label">Passwort</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="sp-input" placeholder="Ihr Passwort" required
              />
            </div>

            <button type="submit" disabled={loading} className="sp-btn-primary w-full py-3 text-[15px] mt-2">
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="sp-spinner" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Anmelden...
                </span>
              ) : (
                'Anmelden'
              )}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-surface-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-xs text-slate-400 font-medium">oder</span></div>
          </div>

          <p className="text-center text-sm text-slate-400">
            Noch kein Konto?{' '}
            <Link to="/register" className="text-brand-500 font-semibold hover:text-brand-600 transition-colors">
              Kostenlos registrieren
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
